import { LABOR_RATES, WOOD_TYPES, PLYWOOD_COSTS, BED_CONFIGS } from '../config/bedConfigs';
import { getBOMForConfig } from '../config/bomTemplates';

export function calculateFabricationLabor(bom, config) {
  const cuts = bom.cuts;
  return (
    cuts.plywoodRips * LABOR_RATES.ripCut +
    cuts.lumberCrossCuts * LABOR_RATES.crossCut +
    cuts.edgeBanding * LABOR_RATES.edgeBanding +
    cuts.countersunkHoles * LABOR_RATES.countersunkHole +
    cuts.pocketHoles * LABOR_RATES.pocketHole
  );
}

export function calculateMaterialCosts(bom, woodType, plywoodType = '11/16" Birch') {
  let total = 0;
  
  // Calculate lumber costs
  bom.lumber.forEach(item => {
    const linearFeet = (item.length / 12) * item.qty;
    const costPerLF = WOOD_TYPES[woodType] || 0;
    total += linearFeet * costPerLF;
  });
  
  // Calculate plywood costs
  bom.plywood.forEach(item => {
    const sheetCost = PLYWOOD_COSTS[`${item.thickness} ${plywoodType.includes('Birch') ? 'Birch' : 'Maple UV'}`] || 0;
    total += item.sheets * sheetCost;
  });
  
  // Calculate hardware costs
  bom.hardware.forEach(item => {
    total += item.qty * item.unitCost;
  });
  
  return total;
}

export function calculateTotalCost(order) {
  const config = BED_CONFIGS[order.bedConfig] || BED_CONFIGS['Twin/Twin'];
  const bom = getBOMForConfig(order.bedConfig);
  
  const materials = calculateMaterialCosts(bom, order.woodType);
  const fabLabor = calculateFabricationLabor(bom, order.bedConfig);
  const paint = config.paint;
  
  // Add addon costs (simplified - you'd have specific costs per addon)
  let addonCost = 0;
  if (order.addons?.includes('Staircase w/ Storage')) addonCost += 200;
  if (order.addons?.includes('Slide')) addonCost += 150;
  if (order.addons?.includes('Trundle')) addonCost += 300;
  if (order.addons?.includes('Desk')) addonCost += 250;
  if (order.addons?.includes('Storage Drawers')) addonCost += 400;
  if (order.addons?.includes('LED Lights')) addonCost += 100;
  
  return {
    materials: materials + addonCost,
    fabLabor,
    paint,
    total: materials + fabLabor + paint + addonCost,
  };
}

export function calculateCommission(order) {
  const config = BED_CONFIGS[order.bedConfig] || BED_CONFIGS['Twin/Twin'];
  const basePrice = config.price;
  
  // Add addon prices (simplified)
  let addonPrice = 0;
  if (order.addons?.includes('Staircase w/ Storage')) addonPrice += 800;
  if (order.addons?.includes('Slide')) addonPrice += 600;
  if (order.addons?.includes('Trundle')) addonPrice += 1200;
  if (order.addons?.includes('Desk')) addonPrice += 1000;
  if (order.addons?.includes('Storage Drawers')) addonPrice += 1500;
  if (order.addons?.includes('LED Lights')) addonPrice += 400;
  
  const totalPrice = basePrice + addonPrice;
  const baseCommission = totalPrice * 0.10;
  const bonusCommission = totalPrice > 15000 ? totalPrice * 0.02 : 0;
  
  return {
    totalPrice,
    baseCommission,
    bonusCommission,
    totalCommission: baseCommission + bonusCommission,
  };
}

export function calculateProfit(order) {
  const costs = calculateTotalCost(order);
  const commission = calculateCommission(order);
  
  const revenue = commission.totalPrice;
  const totalCosts = costs.total + (order.actualDeliveryCost || 0) + (order.actualInstallationCost || 0) + commission.totalCommission;
  const netProfit = revenue - totalCosts;
  const margin = revenue > 0 ? (netProfit / revenue) * 100 : 0;
  
  return {
    revenue,
    materialCosts: costs.materials,
    laborCosts: costs.fabLabor + costs.paint + (order.actualDeliveryCost || 0) + (order.actualInstallationCost || 0),
    deliveryCost: order.actualDeliveryCost || 0,
    installationCost: order.actualInstallationCost || 0,
    salesCommission: commission.totalCommission,
    totalCosts,
    netProfit,
    margin,
  };
}

export function generateOrderNumber() {
  const lastOrder = localStorage.getItem('lastOrderNumber');
  let nextNum = 1;
  
  if (lastOrder) {
    const match = lastOrder.match(/BLX(\d+)/);
    if (match) {
      nextNum = parseInt(match[1], 10) + 1;
    }
  }
  
  return `BLX${String(nextNum).padStart(6, '0')}`;
}

